import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const guestKey = () => 'lucumaa_cart_guest';
const userKey = (userId) => `lucumaa_cart_${userId}`;

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const activeKeyRef = useRef(null);

  // The cart is scoped to the logged-in user, so a customer always sees their
  // own saved cart and never another account's (or a previous session's)
  // items. Guests get a separate cart key.
  const getCartKey = useCallback(() => {
    return user?.id ? userKey(user.id) : guestKey();
  }, [user]);

  const persist = useCallback((key, items) => {
    if (items.length > 0) {
      localStorage.setItem(key, JSON.stringify(items));
    } else {
      localStorage.removeItem(key);
    }
  }, []);

  // Switch carts whenever the signed-in user changes (login/logout/switch).
  useEffect(() => {
    if (!isLoaded) return;
    const nextKey = getCartKey();
    const prevKey = activeKeyRef.current;

    if (prevKey && prevKey !== nextKey) {
      // Persist the previous user's cart under their own key.
      persist(prevKey, cartItems);
    }

    activeKeyRef.current = nextKey;

    let nextItems = [];
    const saved = localStorage.getItem(nextKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) nextItems = parsed;
      } catch (e) {
        console.error('Failed to parse cart:', e);
      }
    }
    setCartItems(nextItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, getCartKey, isLoaded]);

  // Initial load
  useEffect(() => {
    const key = getCartKey();
    activeKeyRef.current = key;
    let nextItems = [];
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          nextItems = parsed;
        }
      } catch (e) {
        console.error('Failed to parse cart:', e);
      }
    }
    setCartItems(nextItems);
    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save cart to the active user's key whenever it changes.
  useEffect(() => {
    if (!isLoaded) return;
    persist(activeKeyRef.current || getCartKey(), cartItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, isLoaded]);

  const addToCart = (item) => {
    // If item already has all required fields (from Glass Configurator), add it directly
    if (item.totalPrice !== undefined || item.basePrice !== undefined) {
      const cartItem = {
        ...item,
        id: item.id || `item-${Date.now()}`,
        quantity: item.quantity || 1,
        addedAt: new Date().toISOString()
      };
      setCartItems(prev => [...prev, cartItem]);
      setIsCartOpen(true);
      return cartItem;
    }
    
    // Legacy support for product objects
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      productId: item.id,
      name: item.name,
      image: item.image,
      basePrice: item.price || item.base_price || 0,
      quantity: 1,
      addedAt: new Date().toISOString()
    };

    setCartItems(prev => [...prev, cartItem]);
    setIsCartOpen(true);
    return cartItem;
  };

  const removeFromCart = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }
    setCartItems(prev => 
      prev.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    if (activeKeyRef.current) {
      localStorage.removeItem(activeKeyRef.current);
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.totalPrice || item.basePrice || 0;
      const qty = item.quantity || 1;
      return total + (price * qty);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + (item.quantity || 1), 0);
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;